export const INTAKE_DRAFT_STORAGE_KEY = 'command:common-intake-draft';

export type IntakeDraftStep =
  | 'define-issue'
  | 'intake-protocol'
  | 'clarify-case'
  | 'context-impact'
  | 'case-ready';

export type IntakeDraftData = {
  issue_title?: string;
  short_description?: string;
  core_constraints?: string[];
  core_constraint_detail?: string;
  decision_needed?: string;
  visibility?: string[];
  impact?: string[];
  time_sensitivity?: string;
  ai_routing_result?: {
    framework: string;
    confidence: number;
  };
};

type StoredIntakeDraft = {
  step: IntakeDraftStep;
  data: Partial<IntakeDraftData>;
};

const INTAKE_STEPS: readonly IntakeDraftStep[] = [
  'define-issue',
  'intake-protocol',
  'clarify-case',
  'context-impact',
  'case-ready',
];

export function hasIntakeDraftContent(data: Partial<IntakeDraftData>): boolean {
  return !!(data.issue_title?.trim() || data.short_description?.trim());
}

function isIntakeDraftStep(step: string): step is IntakeDraftStep {
  return (INTAKE_STEPS as readonly string[]).includes(step);
}

export function loadIntakeDraft(): StoredIntakeDraft | null {
  try {
    const raw = localStorage.getItem(INTAKE_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredIntakeDraft;
    if (!parsed?.step || !isIntakeDraftStep(parsed.step) || !parsed.data) return null;
    if (!hasIntakeDraftContent(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveIntakeDraft(step: IntakeDraftStep, data: Partial<IntakeDraftData>): void {
  if (!hasIntakeDraftContent(data)) return;
  try {
    localStorage.setItem(INTAKE_DRAFT_STORAGE_KEY, JSON.stringify({ step, data } satisfies StoredIntakeDraft));
  } catch {
    // Private mode / quota — intake still works, just no resume
  }
}

export function clearIntakeDraft(): void {
  try {
    localStorage.removeItem(INTAKE_DRAFT_STORAGE_KEY);
  } catch {
    // noop
  }
}

/** §8a — preload title + description from a prior session into intake. */
export function preloadIntakeFromSession(issueTitle: string | null | undefined, issueText: string | null | undefined): void {
  const issue_title = issueTitle?.trim() ?? '';
  const short_description = issueText?.trim() ?? '';
  if (!issue_title && !short_description) return;
  saveIntakeDraft('intake-protocol', { issue_title, short_description });
}
